import React, { useState, useEffect } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { Loader2, FileText, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

interface XBRLData {
  rawXbrlHtml?: string;
  xbrlExtractionTimestamp?: string;
  xbrlConfidence?: number;
  xbrlExtracted: boolean;
  aiExtracted: boolean;
  segmentsExtracted: boolean;
  filingUrl?: string;
}

interface ReportDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  reportId: string;
}

export default function ReportDetailsModal({
  isOpen,
  onClose,
  companyId,
  reportId,
}: ReportDetailsModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [xbrlData, setXbrlData] = useState<XBRLData | null>(null);

  useEffect(() => {
    if (isOpen && reportId) {
      fetchXBRLData();
    }
  }, [isOpen, reportId]);

  const fetchXBRLData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/companies/${companyId}/reports/${reportId}/xbrl`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch XBRL data: ${response.statusText}`);
      }

      const data = await response.json();
      setXbrlData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderExtractionStatus = () => {
    if (!xbrlData) return null;

    return (
      <div className="space-y-2 mb-6">
        <h4 className="text-sm font-semibold text-neutral-700">Extraction Status</h4>
        <div className="grid grid-cols-3 gap-3">
          <div
            className={`flex items-center gap-2 p-3 rounded border ${
              xbrlData.xbrlExtracted
                ? 'bg-green-50 border-green-200'
                : 'bg-neutral-50 border-neutral-200'
            }`}
          >
            {xbrlData.xbrlExtracted ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-neutral-400" />
            )}
            <div>
              <div className="text-xs font-medium text-neutral-700">XBRL Data</div>
              <div className="text-xs text-neutral-500">
                {xbrlData.xbrlExtracted ? 'Extracted' : 'Not extracted'}
              </div>
            </div>
          </div>

          <div
            className={`flex items-center gap-2 p-3 rounded border ${
              xbrlData.aiExtracted
                ? 'bg-green-50 border-green-200'
                : 'bg-neutral-50 border-neutral-200'
            }`}
          >
            {xbrlData.aiExtracted ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-neutral-400" />
            )}
            <div>
              <div className="text-xs font-medium text-neutral-700">AI Extraction</div>
              <div className="text-xs text-neutral-500">
                {xbrlData.aiExtracted ? 'Completed' : 'Pending'}
              </div>
            </div>
          </div>

          <div
            className={`flex items-center gap-2 p-3 rounded border ${
              xbrlData.segmentsExtracted
                ? 'bg-green-50 border-green-200'
                : 'bg-neutral-50 border-neutral-200'
            }`}
          >
            {xbrlData.segmentsExtracted ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-neutral-400" />
            )}
            <div>
              <div className="text-xs font-medium text-neutral-700">Segments</div>
              <div className="text-xs text-neutral-500">
                {xbrlData.segmentsExtracted ? 'Extracted' : 'Not found'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMetadata = () => {
    if (!xbrlData) return null;

    return (
      <div className="space-y-2 mb-6">
        <h4 className="text-sm font-semibold text-neutral-700">Extraction Metadata</h4>
        <div className="bg-neutral-50 rounded p-4 space-y-2">
          {xbrlData.xbrlExtractionTimestamp && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Extraction Time:</span>
              <span className="text-neutral-900 font-mono">
                {new Date(xbrlData.xbrlExtractionTimestamp).toLocaleString()}
              </span>
            </div>
          )}

          {xbrlData.xbrlConfidence !== undefined && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Confidence Score:</span>
              <span className="text-neutral-900 font-mono">
                {xbrlData.xbrlConfidence.toFixed(1)}%
              </span>
            </div>
          )}

          {xbrlData.filingUrl && (
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">SEC Filing URL:</span>
              <a
                href={xbrlData.filingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                View on SEC.gov
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderXBRLContent = () => {
    if (!xbrlData) return null;

    if (!xbrlData.rawXbrlHtml) {
      return (
        <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-amber-900">No XBRL Data Available</div>
            <div className="text-xs text-amber-700 mt-1">
              Raw XBRL HTML was not extracted for this report. This may indicate the report was
              processed before XBRL extraction was implemented.
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-neutral-700">Raw XBRL HTML</h4>
          <div className="text-xs text-neutral-500">
            {xbrlData.rawXbrlHtml.length.toLocaleString()} characters
          </div>
        </div>
        <div className="bg-neutral-900 rounded p-4 overflow-auto max-h-96">
          <pre className="text-xs text-green-400 font-mono whitespace-pre-wrap break-words">
            {xbrlData.rawXbrlHtml}
          </pre>
        </div>
        <div className="text-xs text-neutral-500 italic">
          This is a truncated excerpt showing XBRL-rich sections of the filing for analysis
          purposes.
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Report Details"
      size="xl"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          <span className="ml-3 text-neutral-600">Loading XBRL data...</span>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded">
          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-red-900">Error Loading Data</div>
            <div className="text-xs text-red-700 mt-1">{error}</div>
          </div>
        </div>
      )}

      {!loading && !error && xbrlData && (
        <div className="space-y-6">
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded">
            <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-blue-900">Report ID: {reportId}</div>
              <div className="text-xs text-blue-700 mt-1">
                View detailed extraction status and raw XBRL data for this SEC filing.
              </div>
            </div>
          </div>

          {renderExtractionStatus()}
          {renderMetadata()}
          {renderXBRLContent()}
        </div>
      )}
    </Modal>
  );
}
